import { useEffect, useMemo, useState } from "react";
import { Scenario, type Scenario as ScenarioType } from "@radar-sim/schema";
import { migrateAndParse, ScenarioMigrationError } from "@radar-sim/scenarios";
import {
  buildLibraryEntry,
  deleteScenario,
  listSavedScenarios,
  saveScenario,
  type LibraryEntry,
} from "./library.js";
import {
  SCAFFOLD_BEHAVIOURAL_VESSELS,
  SCAFFOLD_EVENTS,
  SCAFFOLD_SCENARIO,
  SCAFFOLD_SUCCESS_CRITERIA,
  SCAFFOLD_TRACKED_VESSEL,
} from "./scaffolds.js";

export interface ScenarioAuthoringProps {
  /** Optional callback to load the authored scenario in the simulator. */
  onRunScenario?: (scenario: ScenarioType) => void;
}

interface ValidationState {
  ok: boolean;
  scenario: ScenarioType | null;
  errors: string[];
}

function validate(json: string): ValidationState {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch (e) {
    return { ok: false, scenario: null, errors: [`JSON: ${(e as Error).message}`] };
  }
  const result = Scenario.safeParse(parsed);
  if (!result.success) {
    return {
      ok: false,
      scenario: null,
      errors: result.error.issues.map(
        (i) => `${i.path.join(".") || "(root)"}: ${i.message}`,
      ),
    };
  }
  return { ok: true, scenario: result.data, errors: [] };
}

export function ScenarioAuthoring(props: ScenarioAuthoringProps): JSX.Element {
  const [json, setJson] = useState<string>(
    JSON.stringify(SCAFFOLD_SCENARIO, null, 2),
  );
  const [library, setLibrary] = useState<LibraryEntry[]>([]);
  const [importMessage, setImportMessage] = useState<string | null>(null);

  useEffect(() => {
    setLibrary(listSavedScenarios());
  }, []);

  const validation = useMemo(() => validate(json), [json]);

  const insertSnippet = (
    section: "vessels" | "events" | "successCriteria",
    snippet: unknown,
  ) => {
    try {
      const obj = JSON.parse(json) as Record<string, unknown>;
      const list = Array.isArray(obj[section]) ? (obj[section] as unknown[]) : [];
      obj[section] = [...list, snippet];
      setJson(JSON.stringify(obj, null, 2));
    } catch {
      // JSON is currently invalid; the user can fix and retry.
    }
  };

  const handleSave = () => {
    if (!validation.ok || validation.scenario === null) return;
    const entry = buildLibraryEntry(json, validation.scenario);
    setLibrary(saveScenario(entry));
  };

  const handleLoad = (entry: LibraryEntry) => {
    setJson(entry.json);
    setImportMessage(`Loaded "${entry.name}" from library.`);
  };

  const handleDelete = (id: string) => {
    setLibrary(deleteScenario(id));
  };

  const handleExport = () => {
    if (!validation.ok || validation.scenario === null) return;
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${validation.scenario.metadata.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file === undefined) return;
    const text = await file.text();
    try {
      const parsed = JSON.parse(text) as unknown;
      const migrated = migrateAndParse(parsed);
      setJson(JSON.stringify(migrated.scenario, null, 2));
      setImportMessage(
        migrated.appliedMigrations.length === 0
          ? `Imported "${migrated.scenario.metadata.name}".`
          : `Imported and migrated through ${migrated.appliedMigrations.join(", ")}.`,
      );
    } catch (err) {
      const msg =
        err instanceof ScenarioMigrationError
          ? err.message
          : (err as Error).message;
      setImportMessage(`Import failed: ${msg}`);
    }
  };

  const handleRun = () => {
    if (validation.ok && validation.scenario !== null) {
      props.onRunScenario?.(validation.scenario);
    }
  };

  return (
    <div
      style={{
        color: "var(--text)",
        display: "grid",
        gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
        gap: 16,
        width: "100%",
        maxWidth: 1280,
      }}
    >
      <section style={panel}>
        <h2 style={h2}>Scenario JSON</h2>
        <textarea
          value={json}
          onChange={(e) => setJson(e.target.value)}
          spellCheck={false}
          style={{
            width: "100%",
            minHeight: 480,
            background: "var(--bg-elev)",
            color: "var(--text)",
            border: `1px solid ${validation.ok ? "var(--border-strong)" : "var(--danger)"}`,
            borderRadius: "var(--radius-sm)",
            padding: 12,
            fontFamily: "var(--font-mono)",
            fontSize: 12.5,
            lineHeight: 1.5,
            resize: "vertical",
          }}
        />
        <ValidationPanel validation={validation} />
        <Toolbar
          canRun={validation.ok}
          onSave={handleSave}
          onExport={handleExport}
          onRun={handleRun}
          showRun={props.onRunScenario !== undefined}
          onImport={handleImportFile}
          message={importMessage}
        />
      </section>
      <aside style={panel}>
        <ScaffoldButtons onInsert={insertSnippet} />
        <Library
          entries={library}
          onLoad={handleLoad}
          onDelete={handleDelete}
        />
      </aside>
    </div>
  );
}

const panel: React.CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-md)",
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const h2: React.CSSProperties = {
  fontSize: 14,
  margin: 0,
  fontWeight: 600,
  color: "var(--text)",
};

function ValidationPanel(props: { validation: ValidationState }): JSX.Element {
  if (props.validation.ok) {
    return (
      <div style={{ color: "var(--success)", fontSize: 13 }}>
        ✓ Valid scenario · {props.validation.scenario!.vessels.length} vessels ·{" "}
        {props.validation.scenario!.events.length} events ·{" "}
        {props.validation.scenario!.successCriteria.length} criteria
      </div>
    );
  }
  return (
    <div style={{ color: "var(--danger)", fontSize: 13 }}>
      <div>✘ Validation failed</div>
      <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
        {props.validation.errors.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
    </div>
  );
}

function Toolbar(props: {
  canRun: boolean;
  showRun: boolean;
  onSave: () => void;
  onExport: () => void;
  onRun: () => void;
  onImport: (e: React.ChangeEvent<HTMLInputElement>) => void;
  message: string | null;
}): JSX.Element {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <button onClick={props.onSave} disabled={!props.canRun} style={button}>
        Save to library
      </button>
      <button onClick={props.onExport} disabled={!props.canRun} style={button}>
        Export JSON
      </button>
      {props.showRun && (
        <button onClick={props.onRun} disabled={!props.canRun} style={button}>
          Run in simulator
        </button>
      )}
      <label
        style={{
          ...button,
          display: "inline-block",
          cursor: "pointer",
        }}
      >
        Import JSON
        <input
          type="file"
          accept="application/json,.json"
          onChange={props.onImport}
          style={{ display: "none" }}
        />
      </label>
      {props.message !== null && (
        <span style={{ fontSize: 11, opacity: 0.8 }}>{props.message}</span>
      )}
    </div>
  );
}

const button: React.CSSProperties = {
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border-strong)",
  borderRadius: "var(--radius-sm)",
  padding: "7px 12px",
  fontFamily: "inherit",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
};

function ScaffoldButtons(props: {
  onInsert: (
    section: "vessels" | "events" | "successCriteria",
    snippet: unknown,
  ) => void;
}): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <h2 style={h2}>Scaffolds</h2>
      <details>
        <summary style={summary}>Vessels</summary>
        <Group>
          <button
            style={button}
            onClick={() => props.onInsert("vessels", SCAFFOLD_TRACKED_VESSEL)}
          >
            + Tracked vessel
          </button>
          {Object.entries(SCAFFOLD_BEHAVIOURAL_VESSELS).map(([k, v]) => (
            <button
              key={k}
              style={button}
              onClick={() => props.onInsert("vessels", v)}
            >
              + {k}
            </button>
          ))}
        </Group>
      </details>
      <details>
        <summary style={summary}>Events</summary>
        <Group>
          {Object.entries(SCAFFOLD_EVENTS).map(([k, v]) => (
            <button
              key={k}
              style={button}
              onClick={() => props.onInsert("events", v)}
            >
              + {k}
            </button>
          ))}
        </Group>
      </details>
      <details>
        <summary style={summary}>Success criteria</summary>
        <Group>
          {Object.entries(SCAFFOLD_SUCCESS_CRITERIA).map(([k, v]) => (
            <button
              key={k}
              style={button}
              onClick={() => props.onInsert("successCriteria", v)}
            >
              + {k}
            </button>
          ))}
        </Group>
      </details>
    </div>
  );
}

const summary: React.CSSProperties = {
  cursor: "pointer",
  padding: "6px 0",
  fontSize: 13,
  fontWeight: 500,
  color: "var(--text-muted)",
};

function Group(props: { children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
      {props.children}
    </div>
  );
}

function Library(props: {
  entries: LibraryEntry[];
  onLoad: (entry: LibraryEntry) => void;
  onDelete: (id: string) => void;
}): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <h2 style={h2}>Library ({props.entries.length})</h2>
      {props.entries.length === 0 ? (
        <span style={{ fontSize: 11, opacity: 0.6 }}>
          No saved scenarios yet. Save above to populate.
        </span>
      ) : (
        props.entries.map((e) => (
          <div
            key={e.id}
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              padding: "8px 10px",
              background: "var(--bg-elev)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
            }}
          >
            <span style={{ flex: 1, fontSize: 12 }}>
              <strong style={{ color: "var(--text)" }}>{e.name}</strong>
              <br />
              <span
                style={{
                  color: "var(--text-dim)",
                  fontSize: 11,
                  fontFamily: "var(--font-mono)",
                }}
              >
                {e.id}
              </span>
            </span>
            <button onClick={() => props.onLoad(e)} style={button}>
              Load
            </button>
            <button
              onClick={() => props.onDelete(e.id)}
              style={{
                ...button,
                background: "transparent",
                color: "var(--danger)",
                border: "1px solid var(--danger)",
              }}
            >
              Delete
            </button>
          </div>
        ))
      )}
    </div>
  );
}
